import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Tabs,
  Tab,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  List,
  ListItem,
  TextField,
  CircularProgress,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Close,
  ExpandMore,
  Send,
} from "@mui/icons-material";
import {
  PaletteColor,
  PaletteColorHex,
  PaletteColorDarkHex,
} from "../types/PaletteColor.ts";

const PANEL_WIDTH = 420;

// ---- ファイルスコープの内部サブコンポーネント ----

const H3: React.FC<{ children: React.ReactNode; color: string }> = ({
  children,
  color,
}) => (
  <Typography
    variant="subtitle2"
    fontWeight="bold"
    sx={{ mt: 1.5, mb: 0.5, color }}
  >
    {children}
  </Typography>
);

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.7 }}>
    {children}
  </Typography>
);

const Hint: React.FC<{ children: React.ReactNode; color: string }> = ({
  children,
  color,
}) => (
  <Box
    sx={{
      bgcolor: alpha(color, 0.08),
      borderLeft: `3px solid ${color}`,
      p: "6px 12px",
      borderRadius: "0 4px 4px 0",
      mb: 1,
    }}
  >
    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
      {children}
    </Typography>
  </Box>
);

const Warn: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        bgcolor: alpha(theme.palette.warning.main, 0.1),
        borderLeft: `3px solid ${theme.palette.warning.main}`,
        p: "6px 12px",
        borderRadius: "0 4px 4px 0",
        mb: 1,
      }}
    >
      <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
        {children}
      </Typography>
    </Box>
  );
};

interface CompactTableProps {
  color: string;
  headers: string[];
  rows: string[][];
}

const CompactTable: React.FC<CompactTableProps> = ({ color, headers, rows }) => (
  <Box sx={{ overflowX: "auto", mb: 1 }}>
    <Table size="small" sx={{ tableLayout: "auto" }}>
      <TableHead>
        <TableRow sx={{ bgcolor: alpha(color, 0.1) }}>
          {headers.map((h) => (
            <TableCell
              key={h}
              sx={{ fontWeight: "bold", fontSize: "0.75rem", py: 0.5, px: 1 }}
            >
              {h}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>
            {row.map((cell, j) => (
              <TableCell key={j} sx={{ fontSize: "0.8rem", py: 0.5, px: 1 }}>
                {cell}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

interface OlProps {
  color: string;
  items: string[];
}

const Ol: React.FC<OlProps> = ({ color, items }) => (
  <List dense disablePadding sx={{ mb: 1 }}>
    {items.map((item, i) => (
      <ListItem key={i} disableGutters sx={{ alignItems: "flex-start", py: 0.25 }}>
        <Typography sx={{ mr: 1, minWidth: 20, color, fontWeight: "bold" }}>
          {i + 1}.
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          {item}
        </Typography>
      </ListItem>
    ))}
  </List>
);

interface QAProps {
  q: string;
  a: string;
}

const QA: React.FC<QAProps> = ({ q, a }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.25 }}>
      Q. {q}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
      A. {a}
    </Typography>
  </Box>
);

// ---- Accordion セクション ----

interface SectionProps {
  id: string;
  title: string;
  expanded: string | false;
  onChange: (
    id: string
  ) => (e: React.SyntheticEvent, isExpanded: boolean) => void;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({
  id,
  title,
  expanded,
  onChange,
  children,
}) => (
  <Accordion
    expanded={expanded === id}
    onChange={onChange(id)}
    elevation={0}
    disableGutters
    sx={{
      "&:before": { display: "none" },
      borderBottom: "1px solid",
      borderColor: "divider",
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMore />}
      sx={{ px: 2, py: 0.5, minHeight: "auto" }}
    >
      <Typography variant="subtitle1" fontWeight="bold">
        {title}
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ px: 2, pt: 0.5, pb: 2 }}>
      {children}
    </AccordionDetails>
  </Accordion>
);

// ---- AI チャット タブ ----

type ChatMessage = {
  role: "user" | "model";
  text: string;
};

// AI 応答待ち中に流れる詩（200ms 間隔で 1 行ずつ表示）
const POEMS = [
  "The 'encounters' we experience in life are beyond human control.",
  "When, with whom, and why we meet—these things do not happen by design.",
  "They are a matter of fate or destiny that transcends human understanding,",
  "and the very act of meeting is a miracle.",
  "",
  "Until we meet, we reflect on where the other person has been,",
  "what kind of life they have led,",
  "and what joys and sorrows they have experienced.",
  "The phrase 'two stories' conveys a deep respect and compassion for the other person,",
  "acknowledging that both you and they have lived separate, independent lives.",
  "",
  "When weaving cloth, you can never create fabric using only the warp threads or only the weft threads.",
  "A relationship is formed only when 'you' and 'I'—who have lived in different environments—intersect.",
  "It expresses the indispensability of each other's existence;",
  "without one, the other cannot stand.",
  "",
  "The relationship that the two of us have woven together since we met",
  "is not merely for 'our own happiness.'",
  "It is imbued with the hope that one day, this fabric may have the power",
  "to gently envelop, protect, and warm 'someone' other than ourselves.",
  "The bonds between people expand into kindness toward society and others.",
];

interface AiChatTabProps {
  color: string;
  isDark: boolean;
  honbuId?: number;
}

const AiChatTab: React.FC<AiChatTabProps> = ({ color, isDark, honbuId }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [poemLine, setPoemLine] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      setPoemLine(0);
      return;
    }
    const timer = setInterval(() => {
      setPoemLine((prev) => (prev + 1) % POEMS.length);
    }, 200);
    return () => clearInterval(timer);
  }, [isLoading]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading) return;
    const newMessages: ChatMessage[] = [...messages, { role: "user", text }];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);
    try {
      const res = await fetch("/api/helpbot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ honbuId, messages: newMessages }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages((prev) => [...prev, { role: "model", text: data.text }]);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "model", text: "エラーが発生しました。もう一度お試しください。" },
        ]);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "model", text: "通信エラーが発生しました。" },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const aiBubbleBg = isDark ? "#2a2a2a" : "#f5f5f5";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box ref={scrollRef} sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        {messages.length === 0 && !isLoading && (
          <Box sx={{ textAlign: "center", mt: 4, px: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
              ChatisFiles の操作について何でも質問してください。
              <br />
              例:「ファイルをアップロードするには？」
            </Typography>
          </Box>
        )}
        {messages.map((msg, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
              mb: 1,
            }}
          >
            <Box
              sx={{
                maxWidth: "85%",
                px: 1.5,
                py: 1,
                borderRadius:
                  msg.role === "user"
                    ? "12px 12px 2px 12px"
                    : "12px 12px 12px 2px",
                bgcolor: msg.role === "user" ? alpha(color, 0.12) : aiBubbleBg,
              }}
            >
              <Typography
                variant="body2"
                sx={{ lineHeight: 1.7, whiteSpace: "pre-wrap" }}
              >
                {msg.text}
              </Typography>
            </Box>
          </Box>
        ))}
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <CircularProgress size={14} sx={{ color }} />
              <Typography variant="caption" color="text.secondary">
                考え中...
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: "italic", pl: 0.5 }}
            >
              {POEMS[poemLine] || " "}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          p: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          gap: 1,
          alignItems: "flex-end",
        }}
      >
        <TextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="質問を入力...（Shift+Enter で改行）"
          multiline
          maxRows={4}
          fullWidth
          size="small"
          disabled={isLoading}
          sx={{
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": { borderColor: color },
            },
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!input.trim() || isLoading}
          sx={{ color, flexShrink: 0 }}
        >
          <Send />
        </IconButton>
      </Box>
    </Box>
  );
};

// ---- メインコンポーネント ----

export type HelpDrawerProps = {
  open: boolean;
  onClose: () => void;
  paletteColor: PaletteColor;
  isDark?: boolean;
  variant?: "overlay" | "panel";
  honbuId?: number;
};

export const HelpDrawer: React.FC<HelpDrawerProps> = ({
  open,
  onClose,
  paletteColor,
  isDark = false,
  variant = "overlay",
  honbuId,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [expanded, setExpanded] = useState<string | false>("guide-quick");

  const mainColor = isDark
    ? PaletteColorDarkHex[paletteColor]
    : PaletteColorHex[paletteColor];
  const headerBorderColor = isDark ? "#000000" : "#ffffff";

  const handleAccordionChange =
    (id: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? id : false);
    };

  // パネルが開いたとき最初のタブ・セクションにリセット
  useEffect(() => {
    if (open) {
      setActiveTab(0);
      setExpanded("guide-quick");
    }
  }, [open]);

  const tabLabelColor = (index: number) =>
    activeTab === index ? mainColor : undefined;

  const content = (
    <Box
      sx={{
        width: PANEL_WIDTH,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          background: `linear-gradient(to right, ${mainColor}, ${alpha(mainColor, 0.7)})`,
          color: "#fff",
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `3px solid ${headerBorderColor}`,
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          ヘルプ
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
          <Close />
        </IconButton>
      </Box>

      {/* タブバー */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          flexShrink: 0,
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: 40,
        }}
        slotProps={{ indicator: { style: { backgroundColor: mainColor } } }}
      >
        <Tab
          label="使い方ガイド"
          sx={{ minHeight: 40, fontSize: "0.85rem", color: tabLabelColor(0) }}
        />
        <Tab
          label="よくある質問"
          sx={{ minHeight: 40, fontSize: "0.85rem", color: tabLabelColor(1) }}
        />
        <Tab
          label="AIに質問"
          sx={{ minHeight: 40, fontSize: "0.85rem", color: tabLabelColor(2) }}
        />
      </Tabs>

      {/* コンテンツ */}
      <Box
        sx={{
          flex: 1,
          overflowY: activeTab === 2 ? "hidden" : "auto",
          display: activeTab === 2 ? "flex" : "block",
          flexDirection: "column",
        }}
      >
        {/* タブ 0: 使い方ガイド */}
        {activeTab === 0 && (
          <Box>
            <Section
              id="guide-quick"
              title="クイックガイド"
              expanded={expanded}
              onChange={handleAccordionChange}
            >
              <P>よく使う操作の早見表です。</P>
              <CompactTable
                color={mainColor}
                headers={["やりたいこと", "操作場所"]}
                rows={[
                  ["ファイルをアップロード", "「アップロード」ボタン / ドラッグ＆ドロップ"],
                  ["フォルダを作成", "「新規フォルダ」ボタン"],
                  ["ファイルを開く・プレビュー", "ファイル名をクリック"],
                  ["ファイルを検索", "上部の検索バー"],
                  ["ブックマーク登録・解除", "ファイルを右クリック → ブックマーク"],
                  ["ゴミ箱へ移動", "ファイルを右クリック → 削除"],
                  ["ゴミ箱から復元", "左メニュー「ゴミ箱」→ 復元ボタン"],
                  ["権限設定（フォルダ制限）", "フォルダを右クリック → 権限設定"],
                  ["マイフォルダを使う", "左サイドバー「マイフォルダ」"],
                ]}
              />
              <Hint color={mainColor}>
                ? キーを押すといつでもこのヘルプを開け閉めできます。
              </Hint>
            </Section>

            <Section
              id="g1"
              title="① ファイルのアップロード"
              expanded={expanded}
              onChange={handleAccordionChange}
            >
              <Ol
                color={mainColor}
                items={[
                  "アップロードしたいフォルダを開く（フォルダがなければルートのまま）",
                  "「アップロード」ボタンをクリック、またはファイルをブラウザ画面にドラッグ＆ドロップ",
                  "複数ファイルを同時に選択してまとめてアップロードできます",
                  "画面右下の進捗ウィジェットで状況をリアルタイムに確認できます",
                ]}
              />
              <Hint color={mainColor}>
                容量が不足している場合はエラーメッセージが表示されます。
                不要なファイルをゴミ箱から完全削除して容量を確保してください。
              </Hint>
            </Section>

            <Section
              id="g2"
              title="② フォルダの作成・管理"
              expanded={expanded}
              onChange={handleAccordionChange}
            >
              <Ol
                color={mainColor}
                items={[
                  "「新規フォルダ」ボタンをクリックしてフォルダ名を入力",
                  "フォルダを右クリック → 「名前の変更」で名称変更できます",
                  "フォルダを右クリック → 「移動」で別の場所に移動できます",
                  "フォルダを右クリック → 「権限設定」でアクセス制限を設定できます",
                ]}
              />
              <Warn>
                フォルダを削除すると、フォルダ内のすべてのファイルもゴミ箱へ移動されます。
              </Warn>
            </Section>

            <Section
              id="g3"
              title="③ 検索とブックマーク"
              expanded={expanded}
              onChange={handleAccordionChange}
            >
              <H3 color={mainColor}>ファイル検索</H3>
              <P>
                上部の検索バーにキーワードを入力すると、全フォルダを横断してファイルやフォルダを検索できます。
              </P>
              <H3 color={mainColor}>ブックマーク</H3>
              <Ol
                color={mainColor}
                items={[
                  "ファイルやフォルダを右クリック → 「ブックマーク」でお気に入り登録",
                  "左サイドバーの「ブックマーク」からいつでも素早くアクセスできます",
                  "もう一度「ブックマーク」を選択すると解除されます",
                ]}
              />
            </Section>

            <Section
              id="g4"
              title="④ 権限設定（アクセス制限）"
              expanded={expanded}
              onChange={handleAccordionChange}
            >
              <P>
                フォルダにアクセス制限を設定すると、指定したメンバーのみが閲覧・編集できます。
              </P>
              <CompactTable
                color={mainColor}
                headers={["権限レベル", "できること"]}
                rows={[
                  ["制限なし（デフォルト）", "ルームの全メンバーがアクセス可能"],
                  ["閲覧者", "ファイルの閲覧・ダウンロードのみ"],
                  ["編集者", "閲覧に加え、アップロード・削除・リネームが可能"],
                ]}
              />
              <Hint color={mainColor}>
                権限設定はルームの共有フォルダでのみ有効です。マイフォルダは常に自分だけがアクセスできます。
              </Hint>
            </Section>

            <Section
              id="g5"
              title="⑤ ゴミ箱・削除・復元"
              expanded={expanded}
              onChange={handleAccordionChange}
            >
              <Ol
                color={mainColor}
                items={[
                  "ファイルやフォルダを右クリック → 「削除」でゴミ箱へ移動（論理削除）",
                  "左サイドバーの「ゴミ箱」からゴミ箱内のファイルを確認できます",
                  "ゴミ箱内のファイルを「復元」すると元の場所に戻ります",
                  "ゴミ箱内のファイルを「完全削除」するとデータが完全に消去されます",
                ]}
              />
              <Warn>
                完全削除は取り消しができません。削除する前に内容をご確認ください。
              </Warn>
            </Section>
          </Box>
        )}

        {/* タブ 1: よくある質問 */}
        {activeTab === 1 && (
          <Box>
            <Section
              id="faq-file"
              title="ファイル操作について"
              expanded={expanded}
              onChange={handleAccordionChange}
            >
              <QA
                q="ファイルをプレビューするには？"
                a="ファイル名をクリックするとプレビュー画面が表示されます。PDF・画像・動画・テキストなど多くの形式に対応しています。"
              />
              <QA
                q="ファイルをダウンロードするには？"
                a="ファイルを右クリック → 「ダウンロード」を選択するか、詳細パネルのダウンロードボタンを使用してください。"
              />
              <QA
                q="複数ファイルをまとめて操作できますか？"
                a="はい。一括選択モード（右上のチェックボックスアイコン）で複数選択後、まとめてダウンロード・削除が可能です。"
              />
              <QA
                q="ファイルを別のフォルダへ移動するには？"
                a="ファイルを右クリック → 「移動」を選択し、移動先のフォルダを指定してください。別ルームへの移動も可能です。"
              />
              <QA
                q="ファイルをコピーするには？"
                a="ファイルを右クリック → 「コピー」を選択し、コピー先のフォルダを指定してください。別ルームへのコピーも可能です。"
              />
            </Section>

            <Section
              id="faq-folder"
              title="フォルダ・権限について"
              expanded={expanded}
              onChange={handleAccordionChange}
            >
              <QA
                q="制限フォルダとは何ですか？"
                a="アクセス制限が設定されたフォルダです。許可されたメンバーだけが閲覧または編集できます。制限の詳細はフォルダ右クリック → 「権限設定」で確認できます。"
              />
              <QA
                q="マイフォルダとは何ですか？"
                a="自分専用のプライベートフォルダです。他のルームメンバーには見えません。個人の作業ファイルの保存や整理に利用できます。"
              />
              <QA
                q="フォルダの制限を解除するには？"
                a="フォルダを右クリック → 「権限設定」を開き、制限モードをOFFに切り替えてください。編集権限を持つメンバーのみ操作できます。"
              />
              <QA
                q="権限設定ができないフォルダがあります"
                a="マイフォルダ内のフォルダは権限設定の対象外です。また、ルームの編集権限がない場合も操作できません。"
              />
            </Section>

            <Section
              id="faq-storage"
              title="容量・管理について"
              expanded={expanded}
              onChange={handleAccordionChange}
            >
              <QA
                q="現在の使用容量を確認するには？"
                a="左サイドバーのストレージバーで現在の使用量と上限を確認できます。共有フォルダとマイフォルダの容量は別々に管理されています。"
              />
              <QA
                q="「容量が不足しています」というエラーが出た場合は？"
                a="不要なファイルをゴミ箱に移動し、ゴミ箱から完全削除することで容量を解放できます。それでも解決しない場合は施設管理者に容量の増加を依頼してください。"
              />
              <QA
                q="ファイルサイズの上限はありますか？"
                a="施設全体・ルーム・個人それぞれに容量の上限が設定されています。管理者が個別に上限を調整できます。"
              />
            </Section>
          </Box>
        )}

        {/* タブ 2: AI に質問 */}
        {activeTab === 2 && (
          <AiChatTab color={mainColor} isDark={isDark} honbuId={honbuId} />
        )}
      </Box>
    </Box>
  );

  // パネルモード: アニメーション付きインライン表示
  if (variant === "panel") {
    return (
      <Box
        sx={{
          width: open ? PANEL_WIDTH : 0,
          flexShrink: 0,
          overflow: "hidden",
          transition: open
            ? "width 225ms ease-out"
            : "width 195ms cubic-bezier(0.4, 0, 0.6, 1)",
          borderLeft: open ? "1px solid" : "none",
          borderColor: "divider",
        }}
      >
        {content}
      </Box>
    );
  }

  // オーバーレイモード: MUI Drawer
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100vw", sm: PANEL_WIDTH },
          overflow: "hidden",
        },
      }}
    >
      {content}
    </Drawer>
  );
};
